import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { CheckCircle2, Calendar, AlertCircle, User, Mail } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function TaskComplete() {
  const { taskId, token } = useParams();
  const [searchParams] = useSearchParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completedByEmail, setCompletedByEmail] = useState("");
  const [completedByName, setCompletedByName] = useState("");

  useEffect(() => {
    loadTask();
  }, [taskId, token]);

  const loadTask = async () => {
    try {
      const response = await fetch(`${API}/api/public/task-complete/${taskId}/${token}`);
      if (response.ok) {
        const data = await response.json();
        setTask(data);
        if (data.status === "complete") {
          setCompleted(true);
        }
      } else {
        toast.error("Invalid or expired task link");
      }
    } catch (error) {
      console.error("Error loading task:", error);
      toast.error("Failed to load task");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!completedByName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!completedByEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(completedByEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setCompleting(true);
    try {
      const response = await fetch(`${API}/api/public/task-complete/${taskId}/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: completedByEmail,
          name: completedByName
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCompleted(true);
        setTask(data.task);
        
        // Open mailto to send confirmation to requester
        const subject = `Task Completed: ${task.title}`;
        const body = `Hello ${task.creatorName},

I have completed the task you assigned:

Task: ${task.title}
Completed by: ${completedByName} (${completedByEmail})
Completion Date: ${format(new Date(), "dd MMM yyyy h:mm a")}

The task has been marked as complete in SiteProApp.

Best regards,
${completedByName}`;
        
        const mailtoLink = `mailto:${data.creatorEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
        
        toast.success("Task marked as complete! Notification email opened.");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to complete task");
      }
    } catch (error) {
      console.error("Error completing task:", error);
      toast.error("Failed to complete task");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading task...</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Invalid Task Link
            </CardTitle>
            <CardDescription>
              This task link is invalid or has expired. Please contact the person who sent you this link.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-4">
      <div className="max-w-2xl mx-auto py-8">
        <Card className="shadow-lg">
          <CardHeader className="bg-blue-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl">Task Assignment</CardTitle>
            <CardDescription className="text-blue-100">
              Requested by {task.creatorName}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6 space-y-6">
            {completed ? (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-800 mb-2">Task Completed!</h3>
                <p className="text-green-700">
                  This task has been marked as complete. A confirmation email has been sent to {task.creatorName}.
                </p>
              </div>
            ) : (
              <>
                {/* Task Details */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{task.title}</h3>
                    {task.siteName && (
                      <Badge className="mt-2 bg-slate-700">{task.siteName}</Badge>
                    )}
                  </div>

                  {task.description && (
                    <div>
                      <Label className="text-sm text-gray-600">Description</Label>
                      <p className="text-gray-800 mt-1">{task.description}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Scheduled Date
                      </Label>
                      <p className="font-semibold text-gray-900">
                        {format(new Date(task.scheduled_date), "dd MMM yyyy")}
                      </p>
                    </div>

                    <div>
                      <Label className="text-sm text-gray-600">Priority</Label>
                      <Badge className={
                        task.priority === "high" ? "bg-red-500" :
                        task.priority === "medium" ? "bg-yellow-500" :
                        "bg-blue-500"
                      }>
                        {task.priority.toUpperCase()}
                      </Badge>
                    </div>
                  </div>

                  {task.project && (
                    <div>
                      <Label className="text-sm text-gray-600">Project</Label>
                      <p className="text-gray-800">{task.project}</p>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm text-gray-600 flex items-center gap-1">
                      <User className="w-4 h-4" />
                      Requested By
                    </Label>
                    <p className="text-gray-800">{task.creatorName}</p>
                    {task.creatorEmail && (
                      <p className="text-sm text-gray-600">{task.creatorEmail}</p>
                    )}
                  </div>

                  {/* Task Images */}
                  {task.images && task.images.length > 0 && (
                    <div>
                      <Label className="text-sm text-gray-600 mb-2 block">Attachments</Label>
                      <div className="flex gap-2 flex-wrap">
                        {task.images.map((img, idx) => (
                          <img 
                            key={idx}
                            src={img} 
                            alt={`Attachment ${idx + 1}`} 
                            className="w-24 h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                            onClick={() => window.open(img, '_blank')}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Completion Form */}
                <div className="border-t pt-6 space-y-4">
                  <h4 className="font-semibold text-lg text-gray-900">Mark Task as Complete</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={completedByName}
                      onChange={(e) => setCompletedByName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      Your Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={completedByEmail}
                      onChange={(e) => setCompletedByEmail(e.target.value)}
                      required
                    />
                  </div>

                  <Button
                    onClick={handleComplete}
                    disabled={completing}
                    className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                  >
                    {completing ? "Marking Complete..." : "✓ Mark Task Complete"}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    By clicking this button, you confirm that the task has been completed.
                    A notification will be sent to {task.creatorName}.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
